import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import { CSSTransition } from 'react-transition-group';

import ScrollLock from '../ScrollLock';
import { isIOS, declareCustomElement } from '../utils';

import { Context as CloseContext } from './Close';
import styles from './Modal.less';

declareCustomElement('dialog');

const cxb = cx.bind(styles);
const needsSafariFix = (e) => e && isIOS() && /input|textarea|select/i.test(e.target.tagName);
const stopEvent = (e) => e && e.stopPropagation();
const timeout = {
  enter: 500,
  exit: 280,
};

/**
 * This is the Modal Shell of a dialog or prompt. Do not use this directly unless
 * you really want to manage the lower levels.
 *
 * If you are looking to lauch a modal, see {@link Dialog}
 *
 * @private This class should be considered impementation detail of ModalManager.
 * @class Modal
 */
export default class Modal extends React.Component {
  static propTypes = {
    /** @ignore */
    children: PropTypes.any,
    /** @ignore */
    className: PropTypes.string,

    /**
     * A ModalManager instance.
     * @private
     * @ignore
     * @type {ModalManager}
     */
    Manager: PropTypes.object.isRequired,

    /**
     * Sent by ModalManager
     * @ignore
     * @type {Boolean}
     */
    isPortal: PropTypes.bool,

    /**
     * A callback to call on requesting a dismissal.
     *
     * @private
     * @ignore
     * @type {function}
     */
    onDismiss: PropTypes.func.isRequired,

    /**
     * Enable the click listener on the mask that will call onDismiss.
     *
     * @private
     * @ignore
     * @type {boolean}
     */
    closeOnMaskClick: PropTypes.bool,

    /**
     * Don't center the dialog. Position it at the top instead.
     *
     * @private
     * @ignore
     * @type {boolean}
     */
    alignTop: PropTypes.bool,
  };

  static childContextTypes = {
    close: PropTypes.func,
  };

  attachContentRef = (x) => {
    this.content = x;
    if (x) {
      this.focus();
    }
  };

  getChildContext = () => ({
    close: this.close,
  });

  constructor(props) {
    super(props);

    this.props.Manager.addUpdateListener(this.onManagerUpdate);
    this.state = { staging: true };
  }

  componentDidMount() {
    // Staging is here because the ref content isn't set when the modal manager calls isHidden in the render.
    // We set state for another render to occur and correctly call isHidden with a set ref.
    this.setState({ staging: false });
  }

  componentWillUnmount() {
    this.props.Manager.removeUpdateListener(this.onManagerUpdate);
  }

  onManagerUpdate = () => !this.props.isPortal && this.forceUpdate();

  close = (e) => {
    const { onDismiss } = this.props;

    stopEvent(e);

    /* istanbul ignore else */
    if (onDismiss) {
      this.setState({ closing: true });
      setTimeout(() => onDismiss(e), timeout.exit);
    }
  };

  focus = () => {
    this.content.focus();
  };

  onMaskClick = (e) => {
    const { closeOnMaskClick } = this.props;
    if (closeOnMaskClick) {
      this.close(e);
    }
  };

  onFocus = (e) => {
    if (needsSafariFix(e)) {
      this.setState({ safariFix: true });
    }
  };

  onBlur = (e) => {
    if (needsSafariFix(e)) {
      this.setState({ safariFix: false });
    }
  };

  render() {
    const { Manager, children, className, alignTop } = this.props;
    const { safariFix, staging, closing } = this.state;
    const hidden = Manager.isHidden(this);

    const classes = cx(className, cxb('mask', { hidden, safariFix, alignTop }));

    const ios = isIOS();

    return (
      <>
        <ScrollLock />
        <CSSTransition
          key={className}
          in={!staging && !closing}
          classNames={{
            appear: cxb('appear'),
            appearActive: cxb('appear-active'),

            enter: cxb('enter'),
            enterActive: cxb('enter-active'),
            enterDone: cxb('enter-done'),

            exit: cxb('exit'),
            exitActive: cxb('exit-active'),
            exitDone: cxb('exit-done'),
          }}
          timeout={timeout}
          appear={!ios}
          enter={!ios}
          exit={!ios}
          // mountOnEnter
          // unmountOnExit
        >
          <div className={classes} onFocus={this.onFocus} onBlur={this.onBlur} onClick={this.onMaskClick}>
            <dialog className={cxb('content')} open ref={this.attachContentRef} tabIndex="-1" onClick={stopEvent}>
              <CloseContext.Provider value={this.close}>
                {React.Children.map(children, (child) =>
                  typeof child.type === 'string' ? child : React.cloneElement(child, { onDismiss: this.close })
                )}
              </CloseContext.Provider>
            </dialog>
          </div>
        </CSSTransition>
      </>
    );
  }
}
